import { useNavigate } from 'react-router-dom';

import useMonthlyBills from '@/hooks/useMonthlyBills';
import type { MonthlyBill } from '@/types/company.types';
import { dateFormat } from '@/utils/convertDateFormat';
import { Theme } from '@/styles/Theme';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import { getMonthlyBillStatusLabel, getMonthlyBillStatusColor } from '@/utils/monthlyBillStatus';

import { Box, BoxWrapper, SubTitle } from '../style';
import * as styled from './style';

const getPaymentMethodLabel = (method: MonthlyBill['payment_method']) => {
  return method === 'prepaid' ? '선불' : '후불';
};

interface MonthlyBillListProps {
  onBillClick?: (billId: number) => void;
}

const MonthlyBillList = ({ onBillClick }: MonthlyBillListProps) => {
  const { data: monthlyBillsData, isLoading } = useMonthlyBills();
  const navigate = useNavigate();

  const handleBillClick = (billId: number) => {
    if (onBillClick) {
      onBillClick(billId);
    } else {
      navigate(`/payment/bills/${billId}`);
    }
  };

  if (isLoading) {
    return (
      <BoxWrapper>
        <SubTitle>결제 내역</SubTitle>
        <Box>
          <Text fontSize='1rem' color={Theme.colors.G_1}>
            로딩 중...
          </Text>
        </Box>
      </BoxWrapper>
    );
  }

  const bills = monthlyBillsData?.data?.bills || [];

  return (
    <BoxWrapper>
      <SubTitle>결제 내역</SubTitle>
      <Box>
        {bills.length === 0 ? (
          <Text fontSize='1rem' color={Theme.colors.G_1}>
            결제 내역이 없습니다.
          </Text>
        ) : (
          <styled.TableWrapper>
            <styled.Table>
              <styled.Thead>
                <styled.Tr>
                  <styled.Th>청구 기간</styled.Th>
                  <styled.Th>결제 방식</styled.Th>
                  <styled.Th>작업자 수</styled.Th>
                  <styled.Th>금액</styled.Th>
                  <styled.Th>상태</styled.Th>
                  <styled.Th>작업</styled.Th>
                </styled.Tr>
              </styled.Thead>
              <styled.Tbody>
                {bills.map((bill) => (
                  <styled.Tr key={bill.id}>
                    <styled.Td>
                      {dateFormat(bill.billing_period_start)} ~{' '}
                      {dateFormat(bill.billing_period_end)}
                    </styled.Td>
                    <styled.Td>{getPaymentMethodLabel(bill.payment_method)}</styled.Td>
                    <styled.Td>{bill.worker_count}명</styled.Td>
                    <styled.Td>
                      {bill.total_amount.toLocaleString('ko-KR')}원
                    </styled.Td>
                    <styled.Td>
                      <styled.StatusBadge color={getMonthlyBillStatusColor(bill.status)}>
                        {getMonthlyBillStatusLabel(bill.status)}
                      </styled.StatusBadge>
                    </styled.Td>
                    <styled.Td>
                      <Button
                        fontSize='0.875rem'
                        padding='0.375rem 0.75rem'
                        height='2rem'
                        backgroundColor={Theme.colors.B_3}
                        color={Theme.colors.WHITE}
                        onClick={() => handleBillClick(bill.id)}
                      >
                        상세보기
                      </Button>
                    </styled.Td>
                  </styled.Tr>
                ))}
              </styled.Tbody>
            </styled.Table>
          </styled.TableWrapper>
        )}
      </Box>
    </BoxWrapper>
  );
};

export default MonthlyBillList;

